import {
  AnchoredPanelType,
  FormInputGroupLayout,
  InputFormSidePanel,
  InputFormSidePanelProps,
  TextInput,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { ServiceProviders } from "../../constants/pluginConstants";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { LimitDefinitionSummary, LimitScopeValues } from "../../gen/clients/mchub-azure-api-client-platform";
import { getProviderName, optimizedRetryOption } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { getOciRegion } from "../../utils";
import { IntegerInput } from "../IntegerInput/IntegerInput";
import { QuotaSelect } from "../QuotaSelect/QuotaSelect";
import { RegionSelect } from "../RegionSelect/RegionSelect";

export const enum Fields {
  LimitName = "limitName",
  Region = "region",
  CurrentLimit = "currentLimit",
  CurrentUsed = "currentUsed",
  NewLimit = "newLimit",
}

export interface QuotaRequestPanelProps extends
  Pick<InputFormSidePanelProps, "onClose" | "onSubmit"> {
  subscriptionId: string;
  provider: ServiceProviders;
  limitName?: string;
  location?: string;
  newLimit?: number;
}

export const QuotaRequestPanel = ({
  subscriptionId,
  provider,
  limitName,
  location,
  newLimit,
  onSubmit,
  onClose,
}: QuotaRequestPanelProps): JSX.Element => {
  const [limitDefinition, setLimitDefinition] = React.useState<LimitDefinitionSummary>();
  const [remount, setRemount] = React.useState<string>(uniqueGUID());

  let quotaTypeLabel;
  switch (provider) {
    case ServiceProviders.EXADATA:
      quotaTypeLabel = Messages.labels.providerQuotaType(Messages.serviceProviders.exadataDedicated());
      break;
    case ServiceProviders.VMDB:
      quotaTypeLabel = Messages.labels.providerQuotaType(Messages.serviceProviders.vmDatabase());
      break;
    case ServiceProviders.ADBS:
      quotaTypeLabel = Messages.labels.providerQuotaType(Messages.serviceProviders.adbShared());
      break;
    case ServiceProviders.MDS:
      quotaTypeLabel = Messages.labels.providerQuotaType(Messages.serviceProviders.mySqlHeatwave());
      break;
    default:
      break;
  }

  const onDefaultQuotaSet = (selectedLimitDefinition: LimitDefinitionSummary): void => {
    setLimitDefinition(selectedLimitDefinition);
  };

  const onQuotaChange = (_: string, selectedLimitDefinition: LimitDefinitionSummary): void => {
    setLimitDefinition(selectedLimitDefinition);
  };

  const { loading: subscriptionsLoading, subscriptions } = useSubscriptions();
  const subscriptionLocation = subscriptions?.find(sub => sub.id === subscriptionId)?.primaryLocation;

  const { response: limitValueResponse, loading: limitValueLoading, error: limitValueError } = useQueryCall({
    wait: subscriptionsLoading || !(subscriptionId && subscriptionLocation && provider && limitDefinition),
    method: apiClients.withRegion(getOciRegion(subscriptionLocation || "")).limitsApi.listLimitValues,
    options: {
      args: {
        subscriptionId,
        providerName: getProviderName(provider),
        name: limitDefinition?.name || "",
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadQuotasLimitValue(),
      },
    },
  });

  const limitValue = limitValueResponse?.data?.items?.[0];

  // eslint-disable-next-line max-len
  const { response: limitAvailabilityResponse, loading: limitAvailabilityLoading, error: limitAvailabilityError } = useQueryCall({
    wait: subscriptionsLoading || !(subscriptionId && subscriptionLocation && provider && limitDefinition),
    method: apiClients.withRegion(getOciRegion(subscriptionLocation || "")).limitsApi.getResourceAvailability,
    options: {
      args: {
        subscriptionId,
        providerName: getProviderName(provider),
        limitName: limitDefinition?.name || "",
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadQuotasLimitAvailability(),
      },
    },
  });

  const limitAvailability = limitAvailabilityResponse?.data;

  React.useEffect(() => {
    setRemount(uniqueGUID());
  }, [limitAvailability, limitValue]);

  return (
    <InputFormSidePanel
      anchorId="main"
      type={AnchoredPanelType.MEDIUM}
      layout={FormInputGroupLayout.COMPACT}
      title={Messages.createNewPanels.quotaDetails.title()}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <QuotaSelect
        required
        fieldName={Fields.LimitName}
        label={quotaTypeLabel}
        subscriptionId={subscriptionId}
        provider={provider}
        onDefaultValueSet={onDefaultQuotaSet}
        onChange={onQuotaChange}
        defaultValue={limitName ? [limitName] : undefined}
      />
      {limitDefinition?.scopeType === LimitScopeValues.LOCATION && (
        <RegionSelect
          required
          subscriptionId={subscriptionId}
          fieldName={Fields.Region}
          label={Messages.labels.region()}
          tooltip={Messages.hints.toolTipRegion()}
          defaultValue={location ? [location] : undefined}
        />
      )}
      <TextInput
        key={`current-available-${remount}`}
        fieldName={Fields.CurrentLimit}
        label={Messages.labels.currentQuotaLimit(limitDefinition?.description || "")}
        disabled={!limitValue}
        readOnly={!!limitValue}
        defaultValue={limitValue?.value?.toString()}
        loading={limitDefinition && limitValueLoading && !limitValueError}
      />
      <TextInput
        key={`current-used-${remount}`}
        fieldName={Fields.CurrentUsed}
        label={Messages.labels.currentQuotaUsed(limitDefinition?.description || "")}
        disabled={!limitAvailability}
        readOnly={!!limitAvailability}
        defaultValue={limitAvailability && limitAvailability.used == null
          ? Messages.common.unknown()
          : limitAvailability?.used?.toString()}
        loading={limitDefinition && limitAvailabilityLoading && !limitAvailabilityError}
      />
      <IntegerInput
        key={`new-limit-${remount}`}
        fieldName={Fields.NewLimit}
        label={Messages.labels.newQuotaLimit()}
        min={limitValue?.value}
        required
        defaultValue={newLimit || limitValue?.value}
        loading={limitDefinition && limitValueLoading && !limitValueError}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        validator={(value: number): string[] | undefined => {
          const errors: string[] = [];
          if (limitValueError) errors.push(Messages.validation.quotaLimitUnavailable());
          return errors.length > 0 ? errors : undefined;
        }}
      />
    </InputFormSidePanel>
  );
};
